import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import './ConsultantCandidates.css';

const ConsultantCandidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    phoneCode: '+91',
    desiredJobTitle: '',
    skills: '',
    experienceYears: '',
    currentLocation: '',
    resumeUrl: '',
    notes: ''
  });
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const res = await api.get('/consultant/my-candidates');
      setCandidates(res.data || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/consultant/my-candidates', form);
      toast.success('Candidate added');
      setForm({
        name: '', email: '', phone: '', phoneCode: '+91', desiredJobTitle: '',
        skills: '', experienceYears: '', currentLocation: '', resumeUrl: '', notes: ''
      });
      setShowForm(false);
      fetchCandidates();
    } catch (err) {
      toast.error(err.message || 'Failed to add candidate');
    }
  };

  const handleSendInvitation = async (id) => {
    try {
      await api.post(`/consultant/my-candidates/${id}/send-invitation`);
      toast.success('Invitation sent successfully');
      fetchCandidates();
    } catch (err) {
      toast.error(err.message || 'Failed to send invitation');
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDownloadSample = () => {
    const sample = `name,email,phone,phoneCode,desiredJobTitle,skills,experienceYears,currentLocation,resumeUrl,notes
John Doe,john@example.com,9876543210,+91,Software Engineer,"JavaScript, Node.js",3,Bangalore,https://example.com/resume.pdf,Experienced developer
Jane Smith,jane@example.com,9123456789,+91,Data Analyst,"Excel, SQL, Python",2,Delhi,https://example.com/jane.pdf,Fresher data analyst`;

    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample-candidates.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Please select an Excel file');

    const formData = new FormData();
    formData.append('file', file);

    try {
      setImporting(true);
      await api.post('/consultant/my-candidates/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Candidates imported successfully');
      setFile(null);
      setShowImport(false);
      fetchCandidates();
    } catch (err) {
      toast.error(err.message || 'Failed to import candidates');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="consultant-candidates-page">
      <div className="cc-header">
        <div>
          <h2 className="cc-title">My Candidates</h2>
          <p className="cc-subtitle">Add or import candidates, then send invitation emails from the list.</p>
        </div>
        <div className="cc-header-actions">
          <button className="cc-btn cc-btn-secondary" onClick={() => setShowImport(true)}>
            <span className="material-symbols-outlined">upload</span>
            Import
          </button>
          <button className="cc-btn cc-btn-primary" onClick={() => setShowForm(true)}>
            <span className="material-symbols-outlined">person_add</span>
            Add Candidate
          </button>
        </div>
      </div>

      <div className="cc-list-card">
        <h3 className="cc-card-title">Added Candidates ({candidates.length})</h3>
        {loading ? (
          <p className="cc-empty">Loading...</p>
        ) : candidates.length === 0 ? (
          <p className="cc-empty">No candidates added yet.</p>
        ) : (
          <div className="cc-table-wrap">
            <table className="cc-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Desired Role</th>
                  <th>Status</th>
                  <th>Invitation</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c._id}>
                    <td>{c.name}</td>
                    <td>{c.email}</td>
                    <td>{c.desiredJobTitle || '-'}</td>
                    <td>
                      <span className={`cc-status cc-status-${c.status}`}>{c.status}</span>
                    </td>
                    <td>
                      {c.status === 'added' && (
                        <button
                          className="cc-btn cc-btn-small"
                          onClick={() => handleSendInvitation(c._id)}
                        >
                          Send Invitation
                        </button>
                      )}
                      {c.status === 'invited' && (
                        <span className="cc-sent">Sent</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Candidate Modal */}
      {showForm && (
        <div className="cc-modal-overlay add_candidate" onClick={() => setShowForm(false)}>
          <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cc-modal-header">
              <h3 className="cc-card-title">Add Candidate</h3>
              <button type="button" className="cc-modal-close" onClick={() => setShowForm(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="cc-form-row">
                <input name="name" placeholder="Full Name *" value={form.name} onChange={handleChange} required />
                <input name="email" type="email" placeholder="Email *" value={form.email} onChange={handleChange} required />
              </div>
              <div className="cc-form-row">
                <input name="phoneCode" placeholder="+91" value={form.phoneCode} onChange={handleChange} className="cc-small" />
                <input name="phone" placeholder="Phone" value={form.phone} onChange={handleChange} />
              </div>
              <div className="cc-form-row">
                <input name="desiredJobTitle" placeholder="Desired Job Title" value={form.desiredJobTitle} onChange={handleChange} />
                <input name="experienceYears" type="number" placeholder="Experience (Years)" value={form.experienceYears} onChange={handleChange} />
              </div>
              <div className="cc-form-row">
                <input name="skills" placeholder="Skills (comma separated)" value={form.skills} onChange={handleChange} />
                <input name="currentLocation" placeholder="Location" value={form.currentLocation} onChange={handleChange} />
              </div>
              <div className="cc-form-row">
                <input name="resumeUrl" placeholder="Resume URL" value={form.resumeUrl} onChange={handleChange} />
              </div>
              <textarea name="notes" placeholder="Notes" value={form.notes} onChange={handleChange} rows="3" />
              <div className="cc-modal-footer">
                <button type="button" className="cc-btn cc-btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="cc-btn cc-btn-primary">Add Candidate</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Candidates Modal */}
      {showImport && (
        <div className="cc-modal-overlay import_modal" onClick={() => setShowImport(false)}>
          <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cc-modal-header">
              <h3 className="cc-card-title">Import from Excel</h3>
              <button type="button" className="cc-modal-close" onClick={() => setShowImport(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <form onSubmit={handleImport}>
              <p className="cc-hint">File must have <strong>name</strong> and <strong>email</strong> columns. Optional: phone, desiredJobTitle, skills, experienceYears, currentLocation, resumeUrl, notes.</p>
              <div className="cc-import-row">
                <label className="cc-file-label">
                  <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} />
                  <span className="material-symbols-outlined">upload_file</span>
                  {file ? file.name : 'Choose Excel file'}
                </label>
                <button type="button" className="cc-icon-btn" onClick={handleDownloadSample} title="Download Sample CSV">
                  <span className="material-symbols-outlined">download</span>
                </button>
              </div>
              <div className="cc-modal-footer">
                <button type="button" className="cc-btn cc-btn-secondary" onClick={() => setShowImport(false)}>Cancel</button>
                <button type="submit" className="cc-btn cc-btn-primary" disabled={importing || !file}>
                  {importing ? 'Importing...' : 'Import Candidates'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantCandidates;
